import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { API, track } from '../App';

const STORAGE_KEY = 'ckm_email_popup_v1';
const SHOW_AFTER_MS = 12000; // 12 seconds on first visit

/**
 * First-buyer 10% off lead-capture popup. Shows once per visitor (localStorage),
 * skips logged-in users, and posts to /api/leads/email-capture which emails the
 * code via Resend.
 */
export default function EmailCapturePopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip if already shown / dismissed / submitted
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Skip for logged-in users (they don't need a welcome discount)
    if (localStorage.getItem('access_token')) return;

    const t = setTimeout(() => {
      setOpen(true);
      track('Lead', { content_name: 'email_capture_popup_shown' });
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'dismissed');
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/leads/email-capture`, { email, source: 'popup' });
      localStorage.setItem(STORAGE_KEY, 'submitted');
      setDone(true);
      track('CompleteRegistration', { content_name: 'email_capture_popup', value: 10 });
      toast.success(r.data.message || 'Check your inbox for your discount!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your email');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-foreground/50 backdrop-blur-sm p-4"
      data-testid="email-capture-popup"
      onClick={dismiss}
    >
      <div
        className="relative bg-background rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 text-muted-foreground hover:text-foreground"
          data-testid="email-popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-primary to-secondary text-white p-6 text-center">
          <Gift className="h-10 w-10 mx-auto mb-2" />
          <h2 className="text-2xl font-medium font-heading">10% Off Your First Order</h2>
          <p className="text-white/90 text-sm mt-1">Join the Christ Kingdom community</p>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center space-y-3">
              <p className="text-lg font-medium">Check your inbox 📬</p>
              <p className="text-sm text-muted-foreground">
                Your discount code is on its way. Valid for 30 days at checkout.
              </p>
              <Button onClick={dismiss} className="w-full mt-2" data-testid="email-popup-done">
                Start Shopping
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Drop your email and we&apos;ll send you a one-time 10% off code. No spam, just blessings.
              </p>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-popup-input"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="email-popup-submit"
              >
                {submitting ? 'Sending…' : 'Send My 10% Off Code'}
              </Button>
              <button
                type="button"
                onClick={dismiss}
                className="block w-full text-xs text-muted-foreground hover:text-foreground mt-2"
              >
                No thanks, I&apos;d rather pay full price
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
